// src/components/MarketInsights/components/PropertyExplorer.tsx

import React, { useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Chip,
  Avatar,
  Checkbox,
  Skeleton,
  useTheme,
  useMediaQuery,
  alpha,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Pets as PetsIcon,
  Star as StarIcon,
  ChevronRight as ChevronRightIcon,
  Apartment as ApartmentIcon,
} from "@mui/icons-material";
import { PropertyListItem, SortByOption, SortOrder } from "../types";
import {
  formatCurrency,
  formatNumber,
  formatRating,
} from "../utils/formatters";

interface PropertyExplorerProps {
  data: { properties: PropertyListItem[]; total: number } | undefined;
  isLoading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  sortBy: SortByOption;
  sortOrder: SortOrder;
  onSortChange: (sortBy: SortByOption, sortOrder: SortOrder) => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  selectedPropertyIds?: string[];
  onPropertyToggle?: (property: PropertyListItem) => void;
  onPropertyOpen?: (propertyId: string) => void;
  onPropertyClick?: (propertyId: string) => void;
}

const PropertyExplorer: React.FC<PropertyExplorerProps> = ({
  data,
  isLoading,
  search,
  onSearchChange,
  sortBy,
  sortOrder,
  onSortChange,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  selectedPropertyIds = [],
  onPropertyToggle,
  onPropertyOpen,
  onPropertyClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Local state for search input - only updates parent on button click or Enter
  const [searchInput, setSearchInput] = useState(search);

  const handleSort = (column: SortByOption) => {
    if (sortBy === column) {
      onSortChange(column, sortOrder === "asc" ? "desc" : "asc");
    } else {
      onSortChange(column, "desc");
    }
  };

  const getAvailabilityColor = (availNow: number, total: number) => {
    if (total === 0) return "default";
    const rate = (availNow / total) * 100;
    if (rate >= 50) return "success";
    if (rate >= 20) return "warning";
    return "error";
  };

  // Handle search submit (button click or Enter key)
  const handleSearchSubmit = useCallback(() => {
    onSearchChange(searchInput);
  }, [searchInput, onSearchChange]);

  // Handle Enter key press
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSearchSubmit();
      }
    },
    [handleSearchSubmit]
  );

  // Handle clear search
  const handleClearSearch = useCallback(() => {
    setSearchInput("");
    onSearchChange("");
  }, [onSearchChange]);

  const handlePropertyRowClick = useCallback(
    (property: PropertyListItem) => {
      if (onPropertyToggle) {
        onPropertyToggle(property);
      } else if (onPropertyClick) {
        onPropertyClick(property.id);
      }
    },
    [onPropertyClick, onPropertyToggle]
  );

  const handlePropertyOpen = useCallback(
    (event: React.MouseEvent, propertyId: string) => {
      event.stopPropagation();
      if (onPropertyOpen) {
        onPropertyOpen(propertyId);
      } else if (onPropertyClick) {
        onPropertyClick(propertyId);
      }
    },
    [onPropertyClick, onPropertyOpen]
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton width="100%" height={56} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={400} />
        </CardContent>
      </Card>
    );
  }

  const properties = data?.properties || [];
  const total = data?.total || 0;

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Property Explorer
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatNumber(total)} properties found
              {selectedPropertyIds.length > 0
                ? ` · ${selectedPropertyIds.length} selected`
                : ""}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              placeholder="Search properties..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearchSubmit}
              size="medium"
              sx={{ minWidth: 80 }}
            >
              Search
            </Button>
          </Box>
        </Box>

        {isMobile ? (
          <Box>
            {properties.length === 0 ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No properties found matching your criteria
                </Typography>
              </Box>
            ) : (
              properties.map((property, idx, arr) => {
                const isSelected = selectedPropertyIds.includes(property.id);
                const availColor = getAvailabilityColor(
                  property.available_now,
                  property.total_units
                );
                const captionBits: string[] = [];
                if (property.neighborhood) captionBits.push(property.neighborhood);
                captionBits.push(
                  `${formatCurrency(property.min_price)} - ${formatCurrency(property.max_price)}`
                );
                if (property.rating) {
                  captionBits.push(`${property.rating.toFixed(1)}\u2605`);
                }
                return (
                  <Box
                    key={property.id}
                    onClick={() => handlePropertyRowClick(property)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      px: 1.5,
                      py: 1.25,
                      cursor: "pointer",
                      borderBottom:
                        idx < arr.length - 1
                          ? `1px solid ${alpha(theme.palette.divider, 0.6)}`
                          : "none",
                      backgroundColor: isSelected
                        ? alpha(theme.palette.primary.main, 0.08)
                        : "transparent",
                      "&:hover": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          isSelected ? 0.12 : 0.04
                        ),
                      },
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      size="small"
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => handlePropertyRowClick(property)}
                      sx={{ p: 0.25, flexShrink: 0 }}
                    />
                    <Avatar
                      src={property.primary_image_url || undefined}
                      variant="rounded"
                      sx={{
                        width: 44,
                        height: 44,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        flexShrink: 0,
                      }}
                    >
                      <ApartmentIcon color="primary" />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {property.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {captionBits.join(" \u00b7 ")}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${property.available_now} now`}
                      size="small"
                      color={availColor}
                      sx={{
                        flexShrink: 0,
                        height: 22,
                        fontWeight: 600,
                        fontSize: "0.7rem",
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={(event) => handlePropertyOpen(event, property.id)}
                      sx={{ flexShrink: 0 }}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })
            )}
          </Box>
        ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={48} padding="checkbox" />
                <TableCell width={300}>
                  <TableSortLabel
                    active={sortBy === "name"}
                    direction={sortBy === "name" ? sortOrder : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    Property
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortBy === "total_units"}
                    direction={sortBy === "total_units" ? sortOrder : "asc"}
                    onClick={() => handleSort("total_units")}
                  >
                    Units
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortBy === "available_now"}
                    direction={sortBy === "available_now" ? sortOrder : "asc"}
                    onClick={() => handleSort("available_now")}
                  >
                    Available
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === "avg_price"}
                    direction={sortBy === "avg_price" ? sortOrder : "asc"}
                    onClick={() => handleSort("avg_price")}
                  >
                    Price Range
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortBy === "rating"}
                    direction={sortBy === "rating" ? sortOrder : "asc"}
                    onClick={() => handleSort("rating")}
                  >
                    Rating
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Pets</TableCell>
                <TableCell width={50} />
              </TableRow>
            </TableHead>
            <TableBody>
              {properties.map((property) => {
                const isSelected = selectedPropertyIds.includes(property.id);
                return (
                <TableRow
                  key={property.id}
                  hover
                  selected={isSelected}
                  onClick={() => handlePropertyRowClick(property)}
                  sx={{
                    cursor: "pointer",
                    backgroundColor: isSelected
                      ? alpha(theme.palette.primary.main, 0.08)
                      : "inherit",
                    "&:hover": {
                      backgroundColor: alpha(
                        theme.palette.primary.main,
                        isSelected ? 0.12 : 0.04
                      ),
                    },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isSelected}
                      size="small"
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => handlePropertyRowClick(property)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar
                        src={property.primary_image_url || undefined}
                        variant="rounded"
                        sx={{
                          width: 48,
                          height: 48,
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.1
                          ),
                        }}
                      >
                        <ApartmentIcon color="primary" />
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {property.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {property.neighborhood || property.address || "—"}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {formatNumber(property.total_units)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Chip
                        label={`${property.available_now} now`}
                        size="small"
                        color={getAvailabilityColor(
                          property.available_now,
                          property.total_units
                        )}
                        sx={{ minWidth: 70 }}
                      />
                      {property.available_future > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          +{property.available_future} soon
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {formatCurrency(property.min_price)} -{" "}
                        {formatCurrency(property.max_price)}
                      </Typography>
                      {property.avg_price && (
                        <Typography variant="caption" color="text.secondary">
                          Avg: {formatCurrency(property.avg_price)}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {property.rating ? (
                      <Tooltip title={formatRating(property.rating)}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                          }}
                        >
                          <StarIcon
                            sx={{
                              fontSize: 16,
                              color:
                                property.rating >= 4
                                  ? theme.palette.warning.main
                                  : theme.palette.grey[400],
                            }}
                          />
                          <Typography variant="body2">
                            {property.rating.toFixed(1)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {property.pets_allowed ? (
                      <Tooltip title="Pet Friendly">
                        <PetsIcon
                          sx={{
                            fontSize: 20,
                            color: theme.palette.success.main,
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(event) => handlePropertyOpen(event, property.id)}
                    >
                      <ChevronRightIcon color="action" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                );
              })}

              {properties.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No properties found matching your criteria
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        )}

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => onPageChange(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) =>
            onRowsPerPageChange(parseInt(e.target.value, 10))
          }
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </CardContent>
    </Card>
  );
};

export default PropertyExplorer;
